// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"
// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//
// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.

// Polyfill the URLPattern API used by Knock Guides activation rules; not yet
// available in Safari/older browsers. Must load before any guide code runs.
import "urlpattern-polyfill";

import { ActivityBarsTooltip } from "./hooks/ActivityBarsTooltip";
import { BarcodeScanner } from "./hooks/BarcodeScanner";
import { BulkDownload } from "./hooks/BulkDownload";
import { ChatInput } from "./hooks/ChatInput";
import { AgentSidebar, SidebarRestore } from "./hooks/AgentSidebar";
import { AdminPaletteHotkey } from "./hooks/AdminPaletteHotkey";
import { installEarlyAdminPaletteShortcut } from "./hooks/AdminPaletteHotkey.utils";
import { Combobox } from "./hooks/Combobox";
import { DatePicker } from "./hooks/DatePicker";
import { DoubleClickRename } from "./hooks/DoubleClickRename";
import { ElapsedTime } from "./hooks/ElapsedTime";
import { DragScroll } from "./hooks/DragScroll";
import { ColumnOrder } from "./hooks/ColumnOrder";
import { FocusSearch } from "./hooks/FocusSearch";
import { ListFilter } from "./hooks/ListFilter";
import { PersistedToggle } from "./hooks/PersistedToggle";
import { ConfirmDelete } from "./hooks/ConfirmDelete";
import { SidebarCollapse } from "./hooks/SidebarCollapse";
import { SidebarNav } from "./hooks/SidebarNav";
import { VoiceButton } from "./hooks/VoiceButton";
import { CollapseSidebar } from "./hooks/CollapseSidebar";
import { ThemeLab } from "./hooks/ThemeLab";
import { ComputedColour } from "./hooks/ComputedColour";
import { DeriveAction } from "./hooks/DeriveAction";
import { startThemeLive } from "./theme_live";
import { startPylon } from "./pylon";
import { ThemeToggle } from "./hooks/ThemeToggle";
import { ForceDarkMode } from "./hooks/ForceDarkMode";
import { HarViewer } from "./hooks/HarViewer";
import { Chart as UPlotChart } from "./chart-uplot/Chart";
import { Chart as ChartJS } from "./chart.js/Chart";
import { CodeEditorHook } from "./hooks/CodeEditorHook";
import { ConsolePrompt } from "./hooks/ConsolePrompt";
import { Copy } from "./hooks/Copy";
import { ClipboardDispatcher } from "./hooks/ClipboardDispatcher";
import { DownloadReport } from "./hooks/DownloadReport";
import { DevToolsPanel } from "./hooks/DevToolsPanel";
import { FileDownload } from "./hooks/FileDownload";
import { HLSDownload } from "./hooks/HLSDownload";
import { HLSVideo } from "./hooks/HLSVideo";
import { Map } from "./hooks/Map";
import { MegatronScanner } from "./hooks/MegatronScanner";
import { MermaidRender } from "./hooks/MermaidRender";
import { ScrollArea } from "./hooks/ScrollArea";
import { ScrollBottom } from "./hooks/ScrollBottom";
import { ScrollLogs } from "./hooks/ScrollLogs";
import { ScrollTo } from "./hooks/ScrollTo";
import { SandboxPrompt } from "./hooks/SandboxPrompt";
import { Webrtc } from "./hooks/Webrtc";
import { WebrtcTester } from "./hooks/WebrtcTester";
import { Tooltip } from "./hooks/Tooltip";
import { VolumeSlider } from "./hooks/VolumeSlider";
import { RemoteButton } from "./hooks/RemoteButton";
import { RemoteTextInput } from "./hooks/RemoteTextInput";
import { WindowPrompt } from "./hooks/WindowPrompt";
import { DropArea } from "./hooks/DropArea";
import { HoverShowOverlay } from "./hooks/HoverShowOverlay";
import { FocusOnEvent } from "./hooks/FocusOnEvent";
import { TextEditor } from "./hooks/Editor";
import { KeyboardShortcuts } from "./hooks/KeyboardShortcuts";
import { ProcessorController } from "./hooks/ProcessorController";
import { StaticTV } from "./hooks/StaticTV";
import { DragDrop } from "./hooks/DragDrop";
import { BuildUpload } from "./hooks/BuildUpload";
import { LocalStateStore, rememberedFilters } from "./hooks/LocalStateStore";
import { EmbedPostMessageBridge } from "./hooks/EmbedPostMessageBridge";
import { Sankey } from "./hooks/Sankey";
import { PosthogEvent } from "./hooks/PosthogEvent";
import { hooks as colocatedHooks } from "phoenix-colocated/sauron";
import { getHooks } from "live_svelte";
import { createLiveToastHook } from "live_toast";
import posthog from "posthog-js";

import topbar from "../vendor/topbar";
import * as uploaders from "./uploaders";
import "./data-confirm";
// Establish Phoenix Socket and LiveView configuration.
import { Socket } from "phoenix";
import "phoenix_html";
import { LiveSocket } from "phoenix_live_view";
import { addClipboard } from "./utils/clipboard";
import { addLocalStorage } from "./utils/localStorage";
import { isTokenPath, POSTHOG_PERSONAL_DATA_PROPERTIES, redactEvent } from "./utils/url-redaction";
import { inboundBootstrap, stripInboundId } from "./utils/inbound-identity";

import * as Components from "../svelte/*.svelte";

import * as Sentry from "@sentry/browser";

// A page whose own path is a credential (an invitation or magic link) also
// carries it in its form actions, and session replay and autocapture record
// the DOM, which no URL hook reaches. Neither SDK starts there. Everywhere
// else, `redactEvent` blanks credentials in what they send, the referrer from
// such a page included.
const analytics = !isTokenPath(window.location.pathname);

// An embed page is authorized by the `?token=` in its URL, which LiveView
// sends again on every join, so the URL keeps it. Replay records that URL in
// its page meta and in form actions, which no hook reaches, so replay does not
// run there.
const isEmbedded = window.location.pathname.startsWith("/embed");

// Attributes that hold a credential, which replay and autocapture read off the
// DOM and no URL hook sees: a copy button's `phx-click` holds what it copies,
// an invitation link or an API key; a new key's reveal panel holds it in
// `data-token`; the session page's DevTools panel holds its signed URL, and
// its video stream holds the TURN server's username and credential; a HAR
// viewer holds its file's presigned URL.
const CREDENTIAL_ATTRIBUTES = [
  "phx-click",
  "data-token",
  "data-devtools-url",
  "data-ice-servers",
  "data-download-url",
];

const { userEmail, orgUserId, orgId, orgName } = document.body.dataset;

if (analytics) {
  Sentry.init({
    dsn: document.body.dataset.sentryDsn,
    environment: document.body.dataset.sentryEnv,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      ...(isEmbedded
        ? []
        : [
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
              // The first three are the defaults.
              maskAttributes: ["title", "placeholder", "aria-label", ...CREDENTIAL_ATTRIBUTES],
              // `maskAttributes` never applies to a `src`, and masking every
              // `href` would hide every link. Replay records the DevTools
              // iframe's document; it keeps the `src` of the Appium inspector's
              // cross-origin iframe, whose `state` holds the session's proxy
              // token; and a `data-presigned` link's `href` is a presigned URL.
              block: ["#devtools-panel iframe", "#inspector-frame-panel", "[data-presigned]"],
              beforeAddRecordingEvent: redactEvent,
            }),
          ]),
    ],
    // Sentry keeps a breadcrumb's raw values, a console call's arguments
    // included, until an event carries it out.
    beforeBreadcrumb: redactEvent,
    tracePropagationTargets: [/^https:\/\/tvlabs\.ai/],
    tracesSampleRate: 1.0,
    profileSessionSampleRate: 1.0,
    profileLifecycle: "trace",
    // v11 collects cookies, user info and more headers unless told otherwise.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: {
        request: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
        response: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
      },
      httpBodies: [],
      urlQueryParams: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
    },
    // TODO: lower this to something like 0.1?
    // just want to test it out for now
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
  // Replay events reach no `beforeSend*` callback, but they do reach a global
  // event processor, as errors and transactions do.
  Sentry.addEventProcessor(redactEvent);

  posthog.init("phc_6TRQ0ANV3Quaa4qKoElM5R1gqlDRlZHtq7Ia0eFnvkG", {
    // Our nginx proxy running in Fly.io, see the `posthog/` directory
    api_host: "https://ph.tvlabs.ai",
    ui_host: "https://us.posthog.com",
    before_send: redactEvent,
    // `/flags` sends the stored first-page URL as
    // `person_properties.$initial_current_url`. `before_send` never sees that
    // request; these mask the URL before it is stored. The key match is exact.
    mask_personal_data_properties: true,
    custom_personal_data_properties: POSTHOG_PERSONAL_DATA_PROPERTIES,
    autocapture: { element_attribute_ignorelist: CREDENTIAL_ATTRIBUTES },
    // A visitor arriving from device.io carries their anonymous ID in the URL.
    ...inboundBootstrap(window.location.search, Boolean(userEmail)),
  });

  stripInboundId(window.location, window.history);
}

if (analytics && userEmail) {
  Sentry.setUser({ email: userEmail });
  posthog.identify(orgUserId, { email: userEmail, organization: orgName, organizationId: orgId });
}

window.addEventListener("phx:navigate", ({ detail: { href } }) => {
  let url = window.origin + href;
  if (window.location.search) {
    url = url + `?${window.location.search}`;
  }

  posthog.capture("$pageview", { $current_url: url });
});

let client_timezone;
try {
  client_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
} catch (error) {
  console.log("Cannot fetch the user's timezone", error);
}

const csrfTokenMeta = document.querySelector("meta[name='csrf-token']");
let csrfToken = csrfTokenMeta ? csrfTokenMeta.getAttribute("content") : null;

let Hooks = {
  ...colocatedHooks,
  ActivityBarsTooltip,
  BarcodeScanner,
  BulkDownload,
  ChatInput,
  AgentSidebar,
  SidebarRestore,
  ChartJS,
  AdminPaletteHotkey,
  CodeEditorHook,
  Combobox,
  DatePicker,
  ConsolePrompt,
  Copy,
  ClipboardDispatcher,
  DoubleClickRename,
  ElapsedTime,
  DragScroll,
  ColumnOrder,
  FocusSearch,
  ListFilter,
  PersistedToggle,
  ConfirmDelete,
  SidebarCollapse,
  SidebarNav,
  CollapseSidebar,
  ThemeLab,
  ComputedColour,
  DeriveAction,
  ThemeToggle,
  ForceDarkMode,
  DownloadReport,
  DevToolsPanel,
  DragDrop,
  EmbedPostMessageBridge,
  FileDownload,
  FocusOnEvent,
  KeyboardShortcuts,
  HarViewer,
  HLSDownload,
  HLSVideo,
  LiveToast: createLiveToastHook(2000),
  LocalStateStore,
  Map,
  MegatronScanner,
  MermaidRender,
  Sankey,
  PosthogEvent,
  ProcessorController,
  RemoteButton,
  RemoteTextInput,
  SandboxPrompt,
  ScrollArea,
  ScrollBottom,
  ScrollLogs,
  ScrollTo,
  BuildUpload,
  StaticTV,
  SvelteHook: getHooks(Components).SvelteHook,
  TextEditor,
  Tooltip,
  UPlotChart,
  VoiceButton,
  VolumeSlider,
  Webrtc,
  WebrtcTester,
  WindowPrompt,
  DropArea,
  HoverShowOverlay,
};

const socketUrl = isEmbedded ? "/embed/live" : "/live";

const socketParams = isEmbedded ? { client_timezone } : { _csrf_token: csrfToken, client_timezone };

// The Phoenix socket calls this with no element and puts the result in the
// websocket URL; a LiveView join passes its root element. Only joins carry the
// remembered filters.
const connectParams = (el) =>
  el && !isEmbedded ? { ...socketParams, remembered_filters: rememberedFilters() } : socketParams;

let liveSocket = new LiveSocket(socketUrl, Socket, {
  longPollFallbackMs: location.host.startsWith("localhost") ? undefined : 2500,
  disconnectedTimeout: 3000,
  params: connectParams,
  hooks: Hooks,
  uploaders,
});

// Show progress bar on live navigation and form submits
topbar.config({ barColors: { 0: "#29d" }, shadowColor: "rgba(0, 0, 0, .3)" });
window.addEventListener("phx:page-loading-start", (_info) => topbar.show(300));
window.addEventListener("phx:page-loading-stop", (_info) => topbar.hide());
addClipboard();
addLocalStorage();

// Only wired up when the root layout renders the `#theme-live` marker, which
// it does only while a theme preview is active for this session.
if (document.getElementById("theme-live")) {
  startThemeLive();
}

// Catch Cmd/Ctrl+K before the palette hook mounts. The layout head listens
// even earlier, while this file is still downloading; this covers the socket
// join after that.
installEarlyAdminPaletteShortcut();

// connect if there are any LiveViews on the page
liveSocket.connect();

// Deferred to its own task so the widget never holds up connecting the socket
// or the first render.
setTimeout(startPylon);

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket;
//# sourceMappingURL=app.js.map
